/**
 * ソーシャルプラットフォーム定数定義
 * X, YouTube, Twitch, Steam, PlayStation, Xbox などの各種SNS・ゲーミングプラットフォームの設定を定義します。
 *
 * Note: 表示コンポーネントは components/profile/SocialLinks.tsx を参照
 */
import React from 'react';
import XIcon from '@mui/icons-material/X';
import LanguageIcon from '@mui/icons-material/Language';
import CloudIcon from '@mui/icons-material/Cloud';
import { Box } from '@mui/material';

export type SocialPlatform = 'x' | 'yt' | 'ttv' | 'bsky' | 'stm' | 'psn' | 'nsw' | 'xbl' | 'web';

export interface SocialPlatformConfig {
  key: SocialPlatform;
  label: string;
  icon: React.ReactNode;
  placeholder: string;
  urlTemplate: (value: string) => string;
  color: string;
  category: 'sns' | 'gaming';
}

export const SOCIAL_PLATFORMS: SocialPlatformConfig[] = [
  // SNS系
  {
    key: 'x',
    label: 'X (Twitter)',
    icon: <XIcon sx={{ fontSize: '0.875rem' }} />,
    placeholder: 'username',
    urlTemplate: (value) => `https://x.com/${value}`,
    color: '#000000',
    category: 'sns'
  },
  {
    key: 'yt',
    label: 'YouTube',
    icon: (
      <Box
        sx={{
          width: '0.875rem',
          height: '0.875rem',
          maskImage: 'url(/icons/service/youtube.svg)',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskImage: 'url(/icons/service/youtube.svg)',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          backgroundColor: 'currentColor',
        }}
      />
    ),
    placeholder: '@channelname',
    urlTemplate: (value) => `https://youtube.com/@${value}`,
    color: '#FF0000',
    category: 'sns'
  },
  {
    key: 'ttv',
    label: 'Twitch',
    icon: (
      <Box
        sx={{
          width: '0.875rem',
          height: '0.875rem',
          maskImage: 'url(/icons/service/twitch.svg)',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskImage: 'url(/icons/service/twitch.svg)',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          backgroundColor: 'currentColor',
        }}
      />
    ),
    placeholder: 'username',
    urlTemplate: (value) => `https://twitch.tv/${value}`,
    color: '#9146FF',
    category: 'sns'
  },
  {
    key: 'bsky',
    label: 'Bluesky',
    icon: (
      <Box
        sx={{
          width: '0.875rem',
          height: '0.875rem',
          maskImage: 'url(/icons/service/bluesky.svg)',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskImage: 'url(/icons/service/bluesky.svg)',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          backgroundColor: 'currentColor',
        }}
      />
    ),
    placeholder: 'username',
    urlTemplate: (value) => `https://bsky.app/profile/${value}.bsky.social`,
    color: '#1185FE',
    category: 'sns'
  },

  // ゲーミング系
  {
    key: 'stm',
    label: 'Steam',
    icon: (
      <Box
        sx={{
          width: '0.95rem',
          height: '0.95rem',
          maskImage: 'url(/icons/service/steam_black.svg)',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskImage: 'url(/icons/service/steam_black.svg)',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          backgroundColor: 'currentColor',
        }}
      />
    ),
    placeholder: 'steamid or 76561198012345678',
    urlTemplate: (value) => {
      // Steam ID64 は17桁で 7656119 で始まる数字
      // それ以外はカスタム ID として扱う
      const isSteamId64 = /^7656119\d{10}$/.test(value);
      return isSteamId64
        ? `https://steamcommunity.com/profiles/${value}`
        : `https://steamcommunity.com/id/${value}`;
    },
    color: '#00adee',
    category: 'gaming'
  },
  {
    key: 'psn',
    label: 'PlayStation',
    icon: (
      <Box
        sx={{
          width: '0.875rem',
          height: '0.875rem',
          maskImage: 'url(/icons/service/playstation_color.svg)',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskImage: 'url(/icons/service/playstation_color.svg)',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          backgroundColor: 'currentColor',
        }}
      />
    ),
    placeholder: 'psnid',
    urlTemplate: () => '', // PlayStationは公開プロフィールURLがない
    color: '#003791',
    category: 'gaming'
  },
  {
    key: 'nsw',
    label: 'Nintendo Switch',
    icon: (
      <Box
        sx={{
          width: '0.875rem',
          height: '0.875rem',
          maskImage: 'url(/icons/service/switch.svg)',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskImage: 'url(/icons/service/switch.svg)',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          backgroundColor: 'currentColor',
        }}
      />
    ),
    placeholder: 'SW-XXXX-XXXX-XXXX',
    urlTemplate: () => '', // Nintendo Switchは公開プロフィールURLがない
    color: '#E60012',
    category: 'gaming'
  },
  {
    key: 'xbl',
    label: 'Xbox',
    icon: (
      <Box
        sx={{
          width: '0.825rem',
          height: '0.825rem',
          maskImage: 'url(/icons/service/xbox_color.svg)',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskImage: 'url(/icons/service/xbox_color.svg)',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          backgroundColor: 'currentColor',
        }}
      />
    ),
    placeholder: 'gamertag',
    urlTemplate: (value) => `https://www.xbox.com/play/user/${value}`,
    color: '#107C10',
    category: 'gaming'
  },

  // Web
  {
    key: 'web',
    label: 'Website',
    icon: <LanguageIcon sx={{ fontSize: '0.875rem' }} />,
    placeholder: 'https://example.com',
    urlTemplate: (value) => value.startsWith('http') ? value : `https://${value}`,
    color: '#888888',
    category: 'sns'
  },
];

export const getSocialPlatform = (key: SocialPlatform): SocialPlatformConfig | undefined => {
  return SOCIAL_PLATFORMS.find(p => p.key === key);
};
